import Environments, { vpcForEnvironment } from "services/environments";

// Environments shared by the products that live in the original platform account.
const PLATFORM_ENVIRONMENTS = Object.keys(Environments).filter(
  (env) => "ondemand" !== env
);

// EnrollPro ondemand hosts follow the target org environment (see enrollpro
// terraform ondemand_configuration.urls.enrollpro.api).
const ENROLLPRO_ONDEMAND_DOMAINS = {
  qa: "schoolmintqa.net",
  staging: "schoolmintstg.net",
  sandbox: "schoolmint2.net",
};

// Environment products with a fixed data source always copy from.
// TODO: Change to production when we have a production environment
export const ONDEMAND_SOURCE_ENVIRONMENT = "staging";

// Add new on-demand products here. Branches and organizations are only
// fetched for the product the user selected.
export const PRODUCT_CONFIG = {
  enroll: {
    label: "Enroll",
    environments: PLATFORM_ENVIRONMENTS,
    hasFrontend: true,
    hasCustomBranches: true,
    hasDeidentify: true,
    supportsListing: false,
    ondemandDomain: () => "demand.schoolmint.com",
    repos: {
      be: "enrollment-be",
      beCustom: "enrollment-be-custom",
      fe: "enrollment-fe",
      feCustom: "enrollment-fe-custom",
    },
    organizationLabel: (org) =>
      `${org.id} - ${org.organization_name} (${org.subdomain})`,
  },
  sync: {
    label: "Sync Service",
    environments: PLATFORM_ENVIRONMENTS,
    hasFrontend: true,
    hasCustomBranches: false,
    hasDeidentify: false,
    supportsListing: false,
    ondemandDomain: () => "sync.demand.schoolmint.com",
    repos: {
      be: "sis-service",
      fe: "sis-service-fe",
    },
    organizationLabel: (org) => `${org.id} - ${org.subdomain}`,
  },
  enrollpro: {
    label: "EnrollPro (SME)",
    // The environment dropdown picks what the site runs AS, not where data comes
    // from: data is always copied from production, so the value is sent as
    // target_org_environment and organizations are listed from production.
    environments: ["qa", "staging", "sandbox"],
    usesTargetOrgEnvironment: true,
    organizationEnvironment: ONDEMAND_SOURCE_ENVIRONMENT,
    hasFrontend: false,
    hasCustomBranches: false,
    hasDeidentify: false,
    supportsListing: true,
    ondemandDomain: (targetOrgEnvironment) =>
      ENROLLPRO_ONDEMAND_DOMAINS[targetOrgEnvironment],
    repos: {
      be: "schoolmint-be",
    },
    organizationLabel: (org) =>
      `${org.id} - ${org.organization_name} (${org.subdomain})`,
  },
};

/** Product tab order for the Environments inventory page. */
export const PRODUCT_TAB_ORDER = ["enroll", "sync", "enrollpro"];

export const getProductConfig = (product) => PRODUCT_CONFIG[product];

/** Environment whose organization list feeds the org dropdown. */
export const organizationEnvironmentFor = (product, selectedEnvironment) =>
  PRODUCT_CONFIG[product]?.organizationEnvironment ?? selectedEnvironment;

/** Environment a site runs as; rows created before the split lack the new field. */
export const siteTargetEnvironment = (site) =>
  site?.target_org_environment || site?.from_environment;

/** Base domain for an ondemand site host, e.g. demand.schoolmint.com */
export const resolveOndemandDomain = (product, targetOrgEnvironment) =>
  PRODUCT_CONFIG[product]?.ondemandDomain?.(targetOrgEnvironment);

/** Prefer stored hostname; fall back to subdomain + product domain. */
export const buildOndemandLink = (site) => {
  if (!site) {
    return "";
  }
  if (site.hostname) {
    return `https://${site.hostname}`;
  }

  const domain = resolveOndemandDomain(
    site.product,
    siteTargetEnvironment(site)
  );
  if (site.subdomain && domain) {
    return `https://${site.subdomain}.${domain}`;
  }

  return "";
};

/** BFF data-transfer service key for a product + source environment. */
export const dataTransferServiceFor = (environment) =>
  "production" === vpcForEnvironment(environment)
    ? "data-transfer-production"
    : "data-transfer-internal";

export default PRODUCT_CONFIG;
